import { useCallback, useMemo } from 'react';

import {
	type RovoResponseEmojis,
	type RovoResponseLength,
	type RovoResponseStructure,
	type RovoResponseTldr,
	type RovoResponseTone,
	type RovoUserSettings,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useRovoUserSettings } from '../rovo-user-settings';

/**
 * The subset of user settings that make up a user's response style. Field names
 * match `RovoUserSettings` so a patch is forwarded to the settings API unchanged.
 */
export type ResponseStyleSettings = Pick<
	RovoUserSettings,
	| 'responseTone'
	| 'responseLength'
	| 'responseStructure'
	| 'responseEmojis'
	| 'responseTldr'
	| 'customInstructions'
>;

/**
 * Response style with every preference resolved to a concrete value: unset
 * fields fall back to their default so the UI always has something to render.
 * `customInstructions` has no default and stays optional.
 */
export type ResolvedResponseStyle = {
	responseTone: RovoResponseTone;
	responseLength: RovoResponseLength;
	responseStructure: RovoResponseStructure;
	responseEmojis: RovoResponseEmojis;
	responseTldr: RovoResponseTldr;
	customInstructions?: string;
};

/** Defaults applied when the user has not chosen a value. Matches the approved design. */
const DEFAULT_RESPONSE_STYLE = {
	responseTone: 'rovo_default',
	responseLength: 'shorter',
	responseStructure: 'less_structured',
	responseEmojis: 'none',
	responseTldr: 'enabled',
} satisfies Omit<ResolvedResponseStyle, 'customInstructions'>;

/** Per-write success/error handlers, forwarded to the settings mutation. */
export type ResponseStyleWriteCallbacks =
	| {
			onSuccess?: (data: RovoUserSettings) => void;
			onError?: (error: ApiError) => void;
	  }
	| undefined;

export type UseResponseStyleSettingsParams = {
	assistanceServiceParams: AssistanceServiceInputParam['assistanceServiceParams'];
};

export type UseResponseStyleSettingsResult = {
	/** Current response style, with every preference resolved to its default when unset. */
	responseStyle: ResolvedResponseStyle;
	/** True while the user settings are being fetched. */
	isLoading: boolean;
	/** True while a write is in flight. */
	isSaving: boolean;
	setResponseTone: (
		responseTone: RovoResponseTone,
		callbacks?: ResponseStyleWriteCallbacks,
	) => Promise<RovoUserSettings | undefined>;
	setResponseLength: (
		responseLength: RovoResponseLength,
		callbacks?: ResponseStyleWriteCallbacks,
	) => Promise<RovoUserSettings | undefined>;
	setResponseStructure: (
		responseStructure: RovoResponseStructure,
		callbacks?: ResponseStyleWriteCallbacks,
	) => Promise<RovoUserSettings | undefined>;
	setResponseEmojis: (
		responseEmojis: RovoResponseEmojis,
		callbacks?: ResponseStyleWriteCallbacks,
	) => Promise<RovoUserSettings | undefined>;
	setResponseTldr: (
		responseTldr: RovoResponseTldr,
		callbacks?: ResponseStyleWriteCallbacks,
	) => Promise<RovoUserSettings | undefined>;
	setCustomInstructions: (
		customInstructions: string,
		callbacks?: ResponseStyleWriteCallbacks,
	) => Promise<RovoUserSettings | undefined>;
};

/**
 * Reads and writes the user's Rovo response style (tone plus the length,
 * structure, emoji, TL;DR, and custom-instruction preferences) through a single
 * hook.
 *
 * This is the real persistence path: reads come from the fetched user settings
 * and writes go through `updateSettings` as partial updates. The backend does not
 * persist these fields yet, so today reads fall back to the design defaults and a
 * write is a no-op server-side; once the backend serves the fields, reads reflect
 * them and writes persist — with no change here. Consumers own their own
 * optimistic UI (see the `MemorySettingToggle` pattern) and can pass success/error
 * callbacks per write.
 */
export const useResponseStyleSettings = ({
	assistanceServiceParams,
}: UseResponseStyleSettingsParams): UseResponseStyleSettingsResult => {
	const { settings, isFetching, isSubmitting, updateSettings } = useRovoUserSettings({
		assistanceServiceParams,
		enabled: true,
	});

	const responseStyle = useMemo<ResolvedResponseStyle>(
		() => ({
			responseTone: settings?.responseTone ?? DEFAULT_RESPONSE_STYLE.responseTone,
			responseLength: settings?.responseLength ?? DEFAULT_RESPONSE_STYLE.responseLength,
			responseStructure: settings?.responseStructure ?? DEFAULT_RESPONSE_STYLE.responseStructure,
			responseEmojis: settings?.responseEmojis ?? DEFAULT_RESPONSE_STYLE.responseEmojis,
			responseTldr: settings?.responseTldr ?? DEFAULT_RESPONSE_STYLE.responseTldr,
			customInstructions: settings?.customInstructions,
		}),
		[settings],
	);

	const setResponseTone = useCallback(
		(responseTone: RovoResponseTone, callbacks?: ResponseStyleWriteCallbacks) =>
			updateSettings({ responseTone }, callbacks),
		[updateSettings],
	);
	const setResponseLength = useCallback(
		(responseLength: RovoResponseLength, callbacks?: ResponseStyleWriteCallbacks) =>
			updateSettings({ responseLength }, callbacks),
		[updateSettings],
	);
	const setResponseStructure = useCallback(
		(responseStructure: RovoResponseStructure, callbacks?: ResponseStyleWriteCallbacks) =>
			updateSettings({ responseStructure }, callbacks),
		[updateSettings],
	);
	const setResponseEmojis = useCallback(
		(responseEmojis: RovoResponseEmojis, callbacks?: ResponseStyleWriteCallbacks) =>
			updateSettings({ responseEmojis }, callbacks),
		[updateSettings],
	);
	const setResponseTldr = useCallback(
		(responseTldr: RovoResponseTldr, callbacks?: ResponseStyleWriteCallbacks) =>
			updateSettings({ responseTldr }, callbacks),
		[updateSettings],
	);
	const setCustomInstructions = useCallback(
		(customInstructions: string, callbacks?: ResponseStyleWriteCallbacks) =>
			updateSettings({ customInstructions }, callbacks),
		[updateSettings],
	);

	return {
		responseStyle,
		isLoading: isFetching,
		isSaving: isSubmitting,
		setResponseTone,
		setResponseLength,
		setResponseStructure,
		setResponseEmojis,
		setResponseTldr,
		setCustomInstructions,
	};
};
